'use client'
import Dropzone from 'react-dropzone'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { Button } from './ui/button'
import {
  ChooseFileIcon,
  Step1Icon,
  Step2Icon,
  Step3Icon,
} from '@/helper/SVG'
import { X, Lock, XCircle, Phone, FileText } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import ActionButton from './common/ActionButton'
import { Fragment, useState } from 'react'
import Image from 'next/image'
import { consumerRequest, generateAESKey } from '@/helper/request'
import { PATIENT_DOCUMENT_UPLOAD_API } from '@/helper/apiConstants'
import {
  
  SOMETHING_WENT_WRONG,
  CURRENT_SUBSIDIARY,
  SUBSIDIARY_CONFIG
} from '@/utils/constants'
import { dispatch, useSelector } from '@/lib/store'
import useCustomToast from '@/hooks/useCustomToast'
import Link from 'next/link'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import { loadState } from '@/utils/localStorage'
import { encryptAES } from '@/utils/forgeencryption'
import { eventConstants, trackEvent } from '@/utils/events'

const UploadPrescriptionDrawer = ({ open, onOpenChange }: any) => {
  const cityState = useSelector((state) => state.city) || {}
  const userState = useSelector((state) => state?.user) || {}
  const loginState = useSelector((state) => state?.login) || {}
  const commonState = useSelector((state) => state?.common) || {}
  const showToast = useCustomToast()

  const [prescriptionFiles, setPrescriptionFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [commonCenterModalIsOpen, setCommonCenterModalIsOpen] = useState(false)

  const { CUSTOMER_CARE_NUMBER_WITH_TEL } = SUBSIDIARY_CONFIG

  const handleRemoveFile = (fileToDelete: File) => {
    setPrescriptionFiles((prevFiles) =>
      prevFiles.filter((file) => file !== fileToDelete)
    )
  }

  const handleDropAcceptedFiles = (acceptedFiles: File[]) => {
    if (prescriptionFiles.length + acceptedFiles.length > 5) {
      showToast('danger', 'You cannot upload more than 5 files.')
    } else {
      setPrescriptionFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ])
    }
  }

  const handleRedirect = (e: any) => {
    e.preventDefault()
    if (typeof window !== undefined) {
      window.location.href = CUSTOMER_CARE_NUMBER_WITH_TEL
    }
  }

  const handleDropRejectedFiles = (rejectedFiles: any) => {
    if (rejectedFiles?.length > 5) {
      showToast('danger', 'File upload limit exceeded (5 files allowed).')
      return
    }
  }

  const handleUploadPrescription = async () => {
    const aesKey = generateAESKey()
    if (!loginState?.isUserLoggedIn) {
      showToast('danger', 'Please login first')
      return
    }
    if (prescriptionFiles?.length === 0) {
      showToast('danger', 'Please upload the prescription first.')
      return
    }
    const loggedInMobileNumber = loadState('ctxVal') || null

    setLoading(true)
    const formData = new FormData()
    prescriptionFiles.map((file) => {
      formData.append('files', file)
    })
    const payload = {
      phoneNumber: loggedInMobileNumber,
      subsidiary: CURRENT_SUBSIDIARY,
    }
    const { encryptedData, id } = encryptAES(payload, aesKey)
    const encData = { encryptedData, id }
    formData.append('encData', JSON.stringify(encData))
    const apiPath = PATIENT_DOCUMENT_UPLOAD_API

    const response: any = await consumerRequest(apiPath, 'post', formData, {
      accept: '*/*',
      'content-Type': 'multipart/form-data',
    })
    if (response?.status === true) {
      // showToast('success', 'Prescription uploaded successfully')
      setPrescriptionFiles([])
      onOpenChange(false)
      dispatch(
        updateCommonReducerData({
          ...commonState,
          commonCenterModalData: {
            commonCenterModalIsOpen: true,
            message: 'Prescription uploaded successfully',
            description:
              'Stay tuned, our health advisor will call you shortly to create your order',
          },
        })
      )
    const eventPayload = {
      u_mb: loggedInMobileNumber,
      subsidiary: CURRENT_SUBSIDIARY,
    }
      trackEvent(eventConstants.SUBMIT_PRESCRIPTION,eventPayload)
    } else {
      showToast('danger', response?.message || SOMETHING_WENT_WRONG)
    }
    setLoading(false)
    setCommonCenterModalIsOpen(true)
  }

  return (
    <Fragment>
      <Drawer
        direction="right"
        open={open}
        onClose={onOpenChange}
        onOpenChange={onOpenChange}
      >
        <DrawerContent className="mt-0 rounded-none sm:rounded-md md:rounded-xl lg:rounded-xl xl:rounded-xl inset-0 sm:inset-2 md:inset-5 lg:inset-5 xl:inset-5 w-full sm:w-[480px] md:w-[480px] lg:w-[480px] ml-auto upload_prescription">
          <DrawerHeader className="p-5 flex items-center justify-between shadow-elevation3">
            <DrawerTitle className="text-body-lg md:text-heading-md font-semibold">
              Upload Prescription
            </DrawerTitle>
            <DrawerClose asChild>
              <button
                className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center cursor-pointer"
                onClick={() => onOpenChange()}
              >
                <X size={18} className="text-gray-800" />
              </button>
            </DrawerClose>
          </DrawerHeader>
          <div className="py-6 px-4 overflow-y-auto">
            <h3 className="text-body-sm sm:text-body-md lg:text-label-lg text-gray-700 text-center mb-4">
              Have a prescription? Upload here
            </h3>

            <Dropzone
              // onDrop={acceptedFiles => setPrescriptionFiles(acceptedFiles)}
              maxFiles={5}
              onDrop={handleDropAcceptedFiles}
              onDropRejected={handleDropRejectedFiles}
              accept={{
                'image/jpeg': [],
                'image/png': [],
                'application/pdf': [],
              }}
            >
              {({ getRootProps, getInputProps }) => (
                <section className="cursor-pointer">
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <div className="bg-primary-50 border border-solid border-primary-100 rounded-lg h-40 w-full grid place-content-center mb-4">
                      <ChooseFileIcon />
                    </div>
                  </div>
                </section>
              )}
            </Dropzone>
            <div className="bg-gray-100 p-2 px-3 rounded-md flex items-center gap-2 w-fit mx-auto mb-10">
              <Lock size={14} className="text-gray-500" />
              <p className="text-label-xs text-gray-800">
                Your attached prescription is secure with us
              </p>
            </div>
            {prescriptionFiles && prescriptionFiles.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg border border-solid border-gray-200 mb-10">
                <p className="text-label-xs font-semibold text-gray-700 mb-6 uppercase">
                  Attached prescription
                </p>
                <div className="flex gap-4">
                  {prescriptionFiles &&
                    prescriptionFiles.length > 0 &&
                    prescriptionFiles.map((item: any, index: number) => {
                      return (
                        <div key={item.name + 'file' + index}>
                          <div className="relative w-fit">
                            {item.type === 'application/pdf' ? (
                              <div className="border border-solid border-gray-300 rounded-md p-2 shadow-elevation3 bg-white w-[50px] h-[50px]">
                                <FileText size={24} className="text-danger-500" />
                              </div>
                            ) : (
                              <div className="border border-solid border-gray-300 rounded-md p-2 shadow-elevation3 bg-white w-[50px] h-[50px]">
                                <Image
                                  src={item?.preview}
                                  alt="Prescription"
                                  width={32}
                                  height={32}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                  }}
                                />
                              </div>
                            )}
                            <button
                              className="absolute right-[-10px] top-[-10px]"
                              onClick={() => handleRemoveFile(item)}
                            >
                              <XCircle size={18} className="text-danger-500 bg-white rounded-full" />
                            </button>
                          </div>
                          <p className="text-label-xs truncate max-w-[50px]">
                            {item.name}
                          </p>
                        </div>
                      )
                    })}
                </div>
              </div>
            )}

            <Card className="py-4 px-5 rounded-lg border border-solid border-gray-200 h-auto shadow-elevation3 mb-4">
              <CardHeader className="p-0 mb-4">
                <CardTitle className="text-label-lg font-semibold">
                  Why upload a prescription?
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="flex items-center gap-4 mb-6">
                  <Step1Icon />
                  <p className="text-label-sm text-gray-700 w-full">
                    Please upload your prescription in JPG, PDF, or PNG format.
                    The file size must be under 5 MB.
                  </p>
                </div>
                <div className="flex items-center gap-4 mb-6">
                  <Step2Icon />
                  <p className="text-label-sm text-gray-700 w-full">
                    Our experts will verify your prescription and call you to
                    confirm your order.
                  </p>
                </div>
                <div className="flex items-center gap-4 mb-6">
                  <Step3Icon />
                  <p className="text-label-sm text-gray-700 w-full">
                    Your order details will be visible in the profile section
                    after order confirmation.
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="py-4 px-5 rounded-lg border border-solid border-gray-200 h-auto shadow-elevation3 mb-4">
              <CardHeader className="p-0 flex items-center justify-between flex-row mb-0">
                <div>
                  <CardTitle className="text-label-lg font-semibold text-primary">
                    Don’t have a valid prescription?
                  </CardTitle>
                  <CardDescription className="text-label-sm font-semibold">
                    We are here to assist you
                  </CardDescription>
                </div>
                <Link
                  href={CUSTOMER_CARE_NUMBER_WITH_TEL}
                  target="_blank"
                  className="bg-primary-50 rounded-md text-label-sm font-semibold text-primary py-2 px-3 w-fit border border-solid border-primary-100 flex items-center gap-2"
                  onClick={handleRedirect}
                >
                  <Phone size={16} className="text-primary-500 hidden sm:block" />
                  Call us
                </Link>
              </CardHeader>
            </Card>
          </div>
          <div className="py-4 px-5 border-t border-solid border-gray-100">
            <ActionButton
              variant="primary"
              title="Submit Prescription"
              loading={loading}
              disabled={loading}
              className="w-full text-label-lg font-semibold"
              onClick={handleUploadPrescription}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </Fragment>
  )
}

export default UploadPrescriptionDrawer
